import Image from 'next/image'
import { cn } from '@/lib/utils/utils'

interface LogoProps {
    invertColor?: boolean
    iconOnly?: boolean
    className?: string
    /**
     * Eager-load (LCP-friendly). Wordmarks default to `true` (nav is above the fold).
     * Icon-only defaults to `false` unless you pass `priority` (e.g. header mark).
     */
    priority?: boolean
    /**
     * - `default`: inline / compact
     * - `hero`: register, marketing locks
     * - `display`: big welcome / empty-state headlines (matches text-5xl→text-7xl)
     */
    wordmarkSize?: 'default' | 'hero' | 'display'
}

export default function Logo({
    invertColor = false,
    iconOnly = false,
    className,
    priority: priorityProp,
    wordmarkSize = 'default'
}: LogoProps) {
    const priority = priorityProp ?? !iconOnly

    const src = iconOnly
        ? invertColor
            ? '/branding/logo/icon/MWD_LOGO_Icon_White.svg'
            : '/branding/logo/icon/MWD_LOGO_Icon_Dark.svg'
        : invertColor
          ? '/branding/logo/icon-word/MWD_LOGO_IconWord_White.svg'
          : '/branding/logo/icon-word/MWD_LOGO_IconWord_Dark.svg'

    let wordmarkHeights = 'h-7 md:h-9'
    if (wordmarkSize === 'hero') {
        wordmarkHeights = 'h-12 w-auto sm:h-14 md:h-16 lg:h-[4.5rem]'
    } else if (wordmarkSize === 'display') {
        wordmarkHeights = 'h-10 w-auto sm:h-14 md:h-16 lg:h-20 xl:h-24'
    }

    const intrinsicWidth = iconOnly ? 42 : wordmarkSize === 'display' ? 360 : wordmarkSize === 'hero' ? 260 : 176
    const intrinsicHeight = iconOnly ? 42 : wordmarkSize === 'display' ? 78 : wordmarkSize === 'hero' ? 56 : 38

    return (
        <span className={cn('inline-flex items-center', className)}>
            <Image
                src={src}
                alt='myworldesk'
                width={intrinsicWidth}
                height={intrinsicHeight}
                priority={priority}
                loading={priority ? 'eager' : 'lazy'}
                className={cn('h-auto w-auto object-contain', iconOnly ? 'h-9 md:h-10' : wordmarkHeights)}
            />
        </span>
    )
}
